import React, { useState } from 'react';
import { Modal, Form, Button, Alert, Spinner } from 'react-bootstrap';
import validarCPF from '../util/validarCpf';

const validateEmail = emailValue => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(emailValue);
};

export default function RecuperarSenhaModal({
  showResetPasswordModal,
  setShowResetPasswordModal,
  userType,
}) {
  let validationHint = '';
  if (userType === 'externo') {
    validationHint = '(*consulte a administração do ICM*)';
  } else if (userType === 'paciente') {
    validationHint = '(*código disponível no laudo*)';
  }

  const [email, setEmail] = useState('');
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [code, setCode] = useState('');
  const [validacao, setValidacao] = useState('');
  const [cpf, setCPF] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [passwordUpdated, setPasswordUpdated] = useState(false);

  const showFeedback = (variant, message) => setFeedback({ variant, message });

  const handleSendEmail = async () => {
    if (!email || !cpf) {
      showFeedback('warning', 'Dados obrigatórios não preenchidos!');
      return;
    }

    if (!validateEmail(email)) {
      showFeedback('warning', 'Email inválido!');
      return;
    }

    if (!validarCPF(cpf)) {
      showFeedback('warning', 'CPF inválido!');
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${userType}/send/forgot-password-email`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, cpf }),
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        showFeedback(
          'danger',
          data.message || data.error || 'Erro ao enviar email.'
        );
        return;
      }

      setIsEmailSent(true);
      showFeedback('success', data.message || 'Email enviado.');
    } catch (error) {
      showFeedback('danger', error.message || 'Erro ao enviar email.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!cpf) {
      showFeedback('warning', 'CPF é obrigatório!');
      return;
    }
    if (!code) {
      showFeedback('warning', 'Código de verificação é obrigatório!');
      return;
    }
    if (!validacao) {
      showFeedback('warning', 'Código de validação é obrigatório!');
      return;
    }
    if (!newPassword) {
      showFeedback('warning', 'Senha é obrigatória!');
      return;
    }
    if (!confirmNewPassword) {
      showFeedback('warning', 'Confirmação de senha é obrigatória!');
      return;
    }

    if (!validarCPF(cpf)) {
      showFeedback('warning', 'CPF inválido!');
      return;
    }

    if (newPassword.length < 6) {
      showFeedback('warning', 'A senha deve ter no mínimo 6 caracteres!');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      showFeedback('warning', 'As senhas não coincidem!');
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${userType}/reset-password`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cpf, code, validacao, newPassword }),
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        showFeedback(
          'danger',
          data.message || data.error || 'Erro ao atualizar senha.'
        );
        return;
      }

      setPasswordUpdated(true);
      showFeedback('success', data.message || 'Senha atualizada.');
    } catch (error) {
      showFeedback('danger', error.message || 'Erro ao atualizar senha.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setCPF('');
    setCode('');
    setValidacao('');
    setNewPassword('');
    setConfirmNewPassword('');
    setIsEmailSent(false);
    setFeedback(null);
    setPasswordUpdated(false);
    setShowResetPasswordModal(false);
  };

  return (
    <Modal show={showResetPasswordModal} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Registrar senha</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!passwordUpdated && !isEmailSent ? (
          <>
            <Form.Group controlId="resetPasswordEmail">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                autoComplete="email"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                placeholder="Seu email"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </Form.Group>
            <Form.Group controlId="resetPasswordCPF">
              <Form.Label>CPF</Form.Label>
              <Form.Control
                type="text"
                name="cpf"
                inputMode="numeric"
                autoComplete="username"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                placeholder="Seu CPF"
                value={cpf}
                onChange={e => setCPF(e.target.value)}
              />
            </Form.Group>
          </>
        ) : null}
        {!passwordUpdated && isEmailSent ? (
          <>
            <Form.Group controlId="resetPasswordCPF">
              <Form.Label>CPF</Form.Label>
              <Form.Control
                type="text"
                name="cpf"
                inputMode="numeric"
                autoComplete="username"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                placeholder="Seu CPF"
                value={cpf}
                onChange={e => setCPF(e.target.value)}
              />
            </Form.Group>
            <Form.Group controlId="resetPasswordVerificationCode">
              <Form.Label>Código de verificação</Form.Label>
              <Form.Control
                type="text"
                name="verification-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                placeholder="Código recebido por email"
                value={code}
                onChange={e => setCode(e.target.value)}
              />
            </Form.Group>
            <Form.Group controlId="resetPasswordValidationCode">
              <Form.Label>Código de validação {validationHint}</Form.Label>
              <Form.Control
                type="text"
                name="validation-code"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                placeholder="Consulte a administração do ICM"
                value={validacao}
                onChange={e => setValidacao(e.target.value)}
              />
            </Form.Group>
            <Form.Group controlId="resetPasswordNewPassword">
              <Form.Label>Nova senha</Form.Label>
              <Form.Control
                type="password"
                name="new-password"
                autoComplete="new-password"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                placeholder="Sua nova senha"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
              />
            </Form.Group>
            <Form.Group controlId="resetPasswordConfirmPassword">
              <Form.Label>Confirmar senha</Form.Label>
              <Form.Control
                type="password"
                name="confirm-new-password"
                autoComplete="new-password"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                placeholder="Confirme sua senha"
                value={confirmNewPassword}
                onChange={e => setConfirmNewPassword(e.target.value)}
              />
            </Form.Group>
          </>
        ) : null}

        {isEmailSent && !passwordUpdated && (
          <Alert variant="info">
            <strong>
              Você receberá um email com um código de verificação para recuperar
              sua senha.
            </strong>
          </Alert>
        )}
        {feedback && <Alert variant={feedback.variant}>{feedback.message}</Alert>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Fechar
        </Button>
        {!passwordUpdated && (
          <Button
            onClick={isEmailSent ? handleVerifyCode : handleSendEmail}
            disabled={isSubmitting}
          >
            {isEmailSent ? 'Mudar senha' : 'Enviar código'}
            {isSubmitting && (
              <Spinner animation="border" size="sm" className="me-2 ml-2" />
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
